import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'

interface AdVariant {
  id: number
  headline: string
  body: string
  ad_name?: string  // Custom ad name for this variant
  asset_id?: number
  preview_url?: string
  is_approved: boolean
  meta_creative_id?: string
  created_at: string
  original_title?: string
  original_description?: string
}

interface UploadedAsset {
  title: string
  description: string
}

export default function VariantsPage() {
  const { batchId } = useParams<{ batchId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const [selectedVariants, setSelectedVariants] = useState<number[]>([])
  const [approving, setApproving] = useState(false)
  const [variants, setVariants] = useState<AdVariant[]>([])

  const uploadedAssets: UploadedAsset[] = location.state?.uploadedAssets || []

  // Generate variants based on uploaded assets
  useEffect(() => {
    if (uploadedAssets.length > 0) {
      const generatedVariants: AdVariant[] = []
      let variantId = 1

      uploadedAssets.forEach((asset, assetIndex) => {
        // Generate 3-5 variants per asset with different approaches
        const variantTemplates = [
          {
            headline: asset.title,
            body: asset.description
          },
          {
            headline: `🔥 ${asset.title} - Limited Time!`,
            body: `${asset.description} Don't miss out on this exclusive opportunity!`
          },
          {
            headline: `Transform Your Life with ${asset.title}`,
            body: `Join thousands who have already discovered: ${asset.description}`
          },
          {
            headline: `${asset.title} - Get Started Today`,
            body: `Ready to take action? ${asset.description} Click to learn more.`
          },
          {
            headline: `Why Choose ${asset.title}?`,
            body: `The answer is simple: ${asset.description} See the difference for yourself.`
          }
        ]

        variantTemplates.forEach((template, templateIndex) => {
          if (templateIndex < 4) { // Limit to 4 variants per asset
            generatedVariants.push({
              id: variantId++,
              headline: template.headline,
              body: template.body,
              asset_id: assetIndex + 1,
              preview_url: `https://picsum.photos/400/400?random=${assetIndex + templateIndex}`,
              is_approved: false,
              created_at: new Date().toISOString(),
              original_title: asset.title,
              original_description: asset.description,
            })
          }
        })
      })

      setVariants(generatedVariants)
    } else {
      // Fallback to mock data if no uploaded assets
      const mockVariants: AdVariant[] = [
        {
          id: 1,
          headline: "Transform Your Business Today",
          body: "Discover how our platform can revolutionize your workflow and boost productivity by 300%.",
          asset_id: 1,
          preview_url: "https://picsum.photos/400/400?random=1",
          is_approved: false,
          created_at: new Date().toISOString()
        },
        {
          id: 2,
          headline: "Limited Time Offer",
          body: "Get started with our premium features at an unbeatable price. Don't miss out!",
          asset_id: 2,
          preview_url: "https://picsum.photos/400/400?random=2",
          is_approved: false,
          created_at: new Date().toISOString()
        },
        {
          id: 3,
          headline: "Join Thousands of Happy Customers",
          body: "See why businesses worldwide trust our solution for their most important projects.",
          asset_id: 3,
          preview_url: "https://picsum.photos/400/400?random=3",
          is_approved: false,
          created_at: new Date().toISOString()
        }
      ]
      setVariants(mockVariants)
    }
  }, [uploadedAssets])

  const handleVariantSelect = (variantId: number) => {
    setSelectedVariants(prev => 
      prev.includes(variantId)
        ? prev.filter(id => id !== variantId)
        : [...prev, variantId]
    )
  }

  const handleApprove = async () => {
    if (selectedVariants.length === 0) return

    setApproving(true)
    try {
      // API call would go here
      console.log('Approving variants:', selectedVariants)
      
      // Navigate to campaign creation
      navigate('/campaign', { state: { approvedVariants: selectedVariants } })
    } catch (error) {
      console.error('Failed to approve variants:', error)
    } finally {
      setApproving(false)
    }
  }

  return (
    <div className="page-container">
      <div className="page-card">
        {/* Header */}
        <div className="upload-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-4)' }}>
            <button
              onClick={() => navigate('/upload')}
              className="back-btn"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Upload
            </button>
            <div style={{ flex: 1 }} />
          </div>
          <h2 className="upload-title">
            Generated Ad Variants
          </h2>
          <p className="upload-subtitle">
            AI-generated ad variants from your uploaded assets • Batch: {batchId}
          </p>
        </div>

        {/* Selection Summary */}
        {selectedVariants.length > 0 && (
          <div style={{
            background: 'var(--color-secondarySystemFill)',
            borderRadius: '12px',
            padding: 'var(--spacing-4)',
            border: '1px solid var(--color-primary)',
            marginBottom: 'var(--spacing-4)',
          }}>
            <p style={{
              color: 'var(--color-label)',
              fontSize: 'var(--font-size-base)',
              fontWeight: 600,
              margin: 0,
            }}>
              {selectedVariants.length} variant{selectedVariants.length === 1 ? '' : 's'} selected for campaign creation
            </p>
          </div>
        )}

        {/* Variants Grid */}
        <div className="variants-grid">
          {variants.map((variant) => (
            <div
              key={variant.id}
              className={`variant-card ${selectedVariants.includes(variant.id) ? 'selected' : ''}`}
              onClick={() => handleVariantSelect(variant.id)}
            >
              <img
                src={variant.preview_url}
                alt={`Variant ${variant.id}`}
                className="variant-preview"
              />
              <div className="variant-content">
                <h3>{variant.headline}</h3>
                <p>{variant.body}</p>
                <div className="variant-meta">
                  <span className="variant-id">ID: {variant.id}</span>
                  <span className="variant-status">
                    {variant.is_approved ? 'Approved' : 'Pending'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="step-navigation">
          <button
            onClick={() => navigate('/upload')}
            className="btn btn-secondary"
          >
            Generate More Variants
          </button>
          <button
            onClick={handleApprove}
            disabled={selectedVariants.length === 0 || approving}
            className="btn btn-primary"
          >
            {approving ? 'Creating Campaign...' : `Approve ${selectedVariants.length} Variant${selectedVariants.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  )
} 